/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { and, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Options from '@linc-technologies/linced-common/enums/options';
import {
  loadProgressDialData,
  queryProgressionRecords,
} from '@linc-technologies/linced-common/utils/progress-dial';
import {
  getCurrentMilestone,
  subjectProgressions,
  generateStudentProgressData,
} from '@linc-technologies/linced-common/utils/progressions';
import { restartableTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import moment from 'moment';
import { tagName } from '@ember-decorators/component';

// LOAD_ARGS are the arguments that progressDialTask reads.
const LOAD_ARGS = [
  'subject',
  'person',
  'school',
  'endTime',
  'isParentView',
  'levels',
  'expectations',
  'progressions',
  'learnerSummary',
  'developingStates',
  'secureStates',
];

@tagName('')
export default class PrintSubject extends Component {
  @service
  colours;

  @service
  dates;

  @service
  heroApi;

  @service
  options;

  @service
  store;

  @service
  strings;

  @tracked isParentView = false;
  @tracked school = null;

  // developingStates is a list of recent Goal States for in-progress goals.
  developingStates = null;

  // secureStates is a list of Goal States for recently completed goals.
  secureStates = null;

  // includeLegalName specifies whether to include the students legal name.
  @tracked includeLegalName = false;

  // levels specifies the Progress Levels for this Subject.
  levels = null;

  // expectations specifies the Expectations for this Subject.
  expectations = null;

  // progressions specifies the Progressions for this Subject.
  @tracked progressions = null;

  // learnerSummary contains a preloaded Learner Summary for this Subject.
  learnerSummary = null;

  @computed('school')
  get showGoals() {
    return this.options.schoolGet(this.school, Options.GOALS_VISIBLE);
  }

  @computed('school')
  get showAllColours() {
    return this.options.schoolGet(this.school, Options.CHARTS_SHOW_ALL_COLOURS);
  }

  get showYAxisLabels() {
    return this.options.schoolGet(
      this.school,
      Options.CHARTS_SHOW_Y_AXIS_LABELS,
    );
  }

  @computed('school')
  get _showSecureGoals() {
    return this.options.schoolGet(this.school, Options.GOALS_SHOW_SECURE);
  }

  @computed('school')
  get _numSecureGoals() {
    return this.options.schoolGet(this.school, Options.GOALS_NUM_SECURE);
  }

  @computed('school')
  get _showCurrentGoals() {
    return this.options.schoolGet(this.school, Options.GOALS_SHOW_CURRENT);
  }

  @computed('school')
  get _numCurrentGoals() {
    return this.options.schoolGet(this.school, Options.GOALS_NUM_CURRENT);
  }

  @computed('isParentView', '_showSecureGoals')
  get showSecureGoals() {
    return this.isParentView ? this._showSecureGoals : true;
  }

  @computed('isParentView', '_numSecureGoals')
  get numSecureGoals() {
    return this.isParentView ? this._numSecureGoals || 5 : 5;
  }

  @computed('isParentView', '_numCurrentGoals')
  get numCurrentGoals() {
    return this.isParentView ? this._numCurrentGoals || 5 : -1;
  }

  @computed('isParentView', '_showCurrentGoals')
  get showCurrentGoals() {
    return this.isParentView ? this._showCurrentGoals : true;
  }

  @computed('school')
  get progressionsEnabled() {
    return this.options.schoolGet(
      this.school,
      Options.PROGRESS_REPORTING_ENABLED,
    );
  }

  @computed('school')
  get showProgressCharts() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_PROGRESS_CHARTS,
    );
  }

  get showProgressChartKey() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_PROGRESS_CHART_KEY,
    );
  }

  @computed('school')
  get _showAchievementChips() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_SHOW_ACHIEVEMENT_CHIPS,
    );
  }

  @computed('school')
  get _showAchievementChipsStaff() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_SHOW_ACHIEVEMENT_CHIPS_STAFF,
    );
  }

  get showAchievementChips() {
    if (this.isParentView) {
      return this._showAchievementChips;
    }
    return this._showAchievementChipsStaff;
  }

  @computed('school')
  get _showAchievementStatements() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_SHOW_ACHIEVEMENT_STATEMENTS,
    );
  }

  @computed('school')
  get _showAchievementStatementsStaff() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_SHOW_ACHIEVEMENT_STATEMENTS_STAFF,
    );
  }

  @computed(
    'isParentView',
    '_showAchievementStatements',
    '_showAchievementStatementsStaff',
  )
  get showAchievementStatements() {
    if (this.isParentView) {
      return this._showAchievementStatements;
    }
    return this._showAchievementStatementsStaff;
  }

  @computed('school')
  get _showCurrentExpectation() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_SHOW_CURRENT_EXPECTATION,
    );
  }

  @computed('school')
  get _showCurrentExpectationStaff() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_SHOW_CURRENT_EXPECTATION_STAFF,
    );
  }

  @computed(
    'isParentView',
    '_showCurrentExpectation',
    '_showCurrentExpectationStaff',
  )
  get showCurrentExpectation() {
    if (this.isParentView) {
      return this._showCurrentExpectation;
    }
    return this._showCurrentExpectationStaff;
  }

  @computed('school')
  get _showCurriculumLevel() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_INCLUDE_CURRICULUM_LEVEL,
    );
  }

  @computed('school')
  get _showCurriculumLevelStaff() {
    return this.options.schoolGet(
      this.school,
      Options.EXPECTATIONS_INCLUDE_CURRICULUM_LEVEL_STAFF,
    );
  }

  @computed('isParentView', '_showCurriculumLevel', '_showCurriculumLevelStaff')
  get showCurriculumLevel() {
    if (this.isParentView) {
      return this._showCurriculumLevel;
    }
    return this._showCurriculumLevelStaff;
  }

  @computed('school.id')
  get progressStrings() {
    if (!isEmpty(this.strings.getProgressStrings)) {
      // Parent app uses a function to get the right school's progress strings.
      return this.strings.getProgressStrings(this.school);
    }

    // Teacher app gets its strings directly from the strings service.
    return this.strings;
  }

  get _miniGradedCards() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_MINI_GRADED_CARDS,
    );
  }

  @and('_miniGradedCards', 'subject.isGradable')
  miniGradedCards;

  @computed(
    'showGoals',
    'showProgressDials',
    'developingStates.length',
    'secureStates.length',
    'progressDialData',
    'isParentView',
  )
  get showGoalsCard() {
    if (!this.showGoals) {
      return false;
    }

    // Don't show in parent view if it's not visible to parents.
    if (
      this.isParentView &&
      (!this.get('subject.visibleToParents') || !this.showGoals)
    ) {
      return false;
    }

    // If we should show dials and have data, show the card.
    if (this.showProgressDials && !isEmpty(this.progressDialData)) {
      return true;
    }

    // Otherwise, if we don't have any categories, we can't have goals - so don't show it.
    if ((this.get('subject.categories.length') ?? 0) === 0) {
      return false;
    }

    // If we have developing or secure states, show the card.
    if (!isEmpty(this.developingStates) || !isEmpty(this.secureStates)) {
      return true;
    }

    // Otherwise, don't show it.
    return false;
  }

  @computed('includeLegalName', 'person.{legalFirstName,firstName}')
  get firstName() {
    if (this.includeLegalName) {
      return this.get('person.legalFirstName');
    }
    return this.get('person.firstName');
  }

  @computed('school')
  get introCurrent() {
    return this.options.schoolGet(
      this.school,
      'progress:reporting:introCurrent',
    );
  }

  @computed('introCurrent', 'firstName')
  get introCurrentText() {
    const optionText = this.get('introCurrent') || '{{NAME}} is learning to:';

    // Replace the placeholder with the actual name
    if (optionText && !isEmpty(this.firstName)) {
      return optionText.replace(/{{NAME}}/g, this.firstName);
    }

    return optionText;
  }

  @computed('school')
  get introComplete() {
    return this.options.schoolGet(
      this.school,
      'progress:reporting:introComplete',
    );
  }

  @computed('introComplete', 'firstName')
  get introCompleteText() {
    const optionText = this.get('introComplete') || '{{NAME}} is able to:';

    // Replace the placeholder with the actual name
    if (optionText && !isEmpty(this.firstName)) {
      return optionText.replace(/{{NAME}}/g, this.firstName);
    }

    return optionText;
  }

  @computed(
    'firstName',
    'introCurrentText',
    'miniGradedCards',
    'subject.isGradable',
  )
  get currentGoalsTitle() {
    if (this.get('subject.isGradable')) {
      if (this.miniGradedCards) {
        return 'Working on:';
      }
      return this.introCurrentText;
    }
    return `${this.firstName} is learning to:`;
  }

  @computed(
    'firstName',
    'introCompleteText',
    'miniGradedCards',
    'subject.isGradable',
  )
  get recentGoalsTitle() {
    if (this.get('subject.isGradable')) {
      if (this.miniGradedCards) {
        return 'Assessed:';
      }
      return this.introCompleteText;
    }
    return `${this.firstName} is able to:`;
  }

  @computed('showGoals', 'showSecureGoals', 'numSecureGoals', 'secureStates.[]')
  get secureGoals() {
    if (
      !this.showGoals ||
      !this.showSecureGoals ||
      isEmpty(this.secureStates)
    ) {
      // Not set to show secure goals, or no secure goals provided.
      return;
    }

    return this.secureStates
      .filter((state) => state.goal_subject_id === this.subject.id)
      .sort((a, b) =>
        a.createTime > b.createTime ? 1 : a.createTime < b.createTime ? -1 : 0,
      )
      .reverse()
      .slice(0, this.numSecureGoals)
      .reduce((goals, state) => {
        goals.push({
          goal: this.store.peekRecord('goals/goal', state.get('goal_id')),
          state,
        });
        return goals;
      }, []);
  }

  @computed(
    'showGoals',
    'showCurrentGoals',
    'numCurrentGoals',
    'developingStates.[]',
  )
  get currentGoals() {
    if (
      !this.showGoals ||
      !this.showCurrentGoals ||
      isEmpty(this.developingStates)
    ) {
      // Not set to show current goals, or no current goals provided.
      return;
    }

    let numCurrentGoals =
      this.numCurrentGoals === -1 ? undefined : this.numCurrentGoals;
    return this.developingStates
      .filter((state) => state.goal_subject_id === this.subject.id)
      .sort((a, b) =>
        a.createTime > b.createTime ? 1 : a.createTime < b.createTime ? -1 : 0,
      )
      .reverse()
      .slice(0, numCurrentGoals)
      .reduce((goals, state) => {
        goals.push(this.store.peekRecord('goals/goal', state.get('goal_id')));
        return goals;
      }, []);
  }

  @computed(
    'subject.showRadialDials',
    'developingStates.length',
    'secureStates.length',
  )
  get showProgressDials() {
    if (this.subject.showRadialDials) {
      // If enabled, only actually display if the student is working on or has completed any goals.
      return !isEmpty(this.developingStates) || !isEmpty(this.secureStates);
    }
    return false;
  }

  // recordsTask queries the expectations and progressions that the caller
  // does not give.
  recordsTask = restartableTask(async () => {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }
    return await queryProgressionRecords(
      this.store,
      {
        goalSubjectId: this.subject.id,
        personId: this.person.id,
        schoolId: this.school.id,
        asAtTime: this.endTime,
      },
      {
        expectations: isEmpty(this.expectations),
        progressions: isEmpty(this.progressions),
      },
    );
  });

  // progressDialTask loads the records, then the progress dial data.
  // Arguments change them, so load performs it.
  progressDialTask = restartableTask(async () => {
    await this.recordsTask.perform();
    return await loadProgressDialData(
      this,
      this.school?.id,
      this.person?.id,
      this.endTime,
    );
  });

  // load performs progressDialTask on the first render, and again when an
  // argument that it reads changes.
  load = modifier(() => {
    this.getProperties(LOAD_ARGS);
    this.progressDialTask.perform();
  });

  get progressDialData() {
    return this.progressDialTask.lastSuccessful?.value;
  }

  get progressChartColors() {
    return this.colours.charts(this.school);
  }

  @alias('subject.milestones')
  sortedMilestones;

  @computed(
    'sortedMilestones.[]',
    'person.{weeksAtSchool,monthsAtSchool,currentYearLevel}',
    'endTime',
  )
  get currentMilestone() {
    let asAtDate;
    if (this.endTime) {
      asAtDate = moment.unix(this.endTime).format('YYYY-MM-DD');
    }
    return getCurrentMilestone(
      this.sortedMilestones,
      this.person,
      this.dates.rolloverMonthDate,
      asAtDate,
    );
  }

  @computed('currentMilestone', 'sortedMilestones.[]')
  get previousMilestone() {
    let currentIndex = this.sortedMilestones.indexOf(this.currentMilestone);
    return this.sortedMilestones.at(currentIndex - 1);
  }

  @computed(
    'progressionsEnabled',
    'subject.{id,trackable}',
    'expectations',
    'recordsTask.lastSuccessful',
  )
  get expectationRecords() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }

    if (!isEmpty(this.expectations)) {
      // Expectations have been provided; use them.
      return this.expectations;
    }

    // Fetched, as we weren't given any.
    return this.recordsTask.lastSuccessful?.value?.expectations ?? [];
  }

  @computed(
    'progressionsEnabled',
    'subject.{id,trackable}',
    'progressions',
    'recordsTask.lastSuccessful',
  )
  get progressionRecords() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }

    if (!isEmpty(this.progressions)) {
      // Progressions have been provided; use them.
      return this.progressions;
    }

    // Fetched, as we weren't given any.
    return this.recordsTask.lastSuccessful?.value?.progressions ?? [];
  }

  @computed(
    'progressionsEnabled',
    'subject.{id,trackable,milestones.[]}',
    'person.id',
    'levels.[]',
    'expectationRecords.[]',
    'progressionRecords.[]',
    'showAllColours',
  )
  get progressData() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }

    let milestones = this.get('subject.milestones');
    let levels = this.levels;
    let expectations = this.expectationRecords;
    if (isEmpty(milestones) || isEmpty(levels) || isEmpty(expectations)) {
      // We need Milestones, Levels and Expectations to display anything.
      return;
    }

    // Generate progression data for this Subject
    let progressions = this.progressionRecords;
    if (!isEmpty(progressions)) {
      return generateStudentProgressData(
        expectations,
        milestones,
        levels,
        progressions,
        this.showAllColours,
      );
    }
  }

  // statementData needs generating if we're not also displaying progress dials.
  @computed(
    'subject.{id,trackable}',
    'showProgressDials',
    'levels.[]',
    'expectationRecords.[]',
    'progressionRecords.[]',
    'displayData.{expectation,progression,milestone}',
    'progressDialData',
  )
  get statementData() {
    if (!this.subject.trackable) {
      return;
    }
    if (this.showProgressDials) {
      return this.progressDialData;
    }

    if (isEmpty(this.levels) || isEmpty(this.expectationRecords)) {
      // We need Levels and Expectations to display anything.
      return;
    }

    let { expectation, progression } = this.displayData;
    return subjectProgressions(
      this.subject,
      this.levels,
      expectation,
      progression,
    );
  }

  // statementMilestone returns the milestone to display in the achievement/expectation statement.
  @alias('displayData.milestone')
  statementMilestone;

  // displayData returns the expectation, progression and milestone to be displayed in statements etc.
  @computed(
    'subject.id',
    'person.id',
    'levels.[]',
    'expectationRecords.[]',
    'progressionRecords.[]',
    'currentMilestone',
    'previousMilestone',
  )
  get displayData() {
    let milestone = this.currentMilestone;
    let expectation = this.expectationRecords.find(
      (record) => record.milestone_id === milestone.id,
    );
    let progression = this.progressionRecords.find(
      (record) => record.milestone_id === milestone.id,
    );

    // Use previous milestone if there's no data for the current one (but is for the previous).
    if (isEmpty(progression) && !isEmpty(this.previousMilestone)) {
      let prevMilestone = this.previousMilestone;
      let prevExpectation = this.expectationRecords.find(
        (record) => record.milestone_id === prevMilestone.id,
      );
      let prevProgression = this.progressionRecords.find(
        (record) => record.milestone_id === prevMilestone.id,
      );
      if (!isEmpty(prevProgression)) {
        milestone = prevMilestone;
        expectation = prevExpectation;
        progression = prevProgression;
      }
    }

    return { expectation, progression, milestone };
  }

  @computed(
    'progressionsEnabled',
    'isParentView',
    'showAchievementStatements',
    'showProgressCharts',
    'showCurrentExpectation',
    'showCurriculumLevel',
    'subject.{trackable,visibleToParents}',
    'progressions',
    'statementData',
    'miniGradedCards',
  )
  get showProgress() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      // Not enabled.
      return false;
    }
    if (this.miniGradedCards) {
      // Graded subject with mini cards.  Don't show Progress.
      return false;
    }
    if (isEmpty(this.progressions) || isEmpty(this.statementData)) {
      // No data.
      return false;
    }
    if (!this.isParentView) {
      // Show progress for staff (not using Caregiver View).
      return true;
    }
    if (!this.subject.visibleToParents) {
      // Not visible to caregivers.
      return false;
    }
    // Show progress if any subcomponent is to set to be shown.
    return (
      this.showAchievementStatements ||
      this.showProgressCharts ||
      this.showCurrentExpectation ||
      this.showCurriculumLevel
    );
  }
}
